import React from 'react';
import { Switch } from 'react-router-dom';
import { EnterprisePaths } from 'src/components/AppRoutes/Enterprise/EnterprisePathsDictionary';
import { MealCreditRoutes } from 'src/components/AppRoutes/MealCreditRoutes';
import { CorporatePaths } from 'src/components/AppRoutes/Enterprise/CorporatePaths';
import { CorporateRoutes } from 'src/components/AppRoutes/CorporateRoutes';
import WrappedRoute from 'src/components/AppRoutes/WrappedRoute';

import withEnterpriseLayout from 'src/components/AppRoutes/Enterprise/withEnterpriseLayout';
import EnterpriseDepartmentCreate from 'src/components/EnterpriseDepartmentCreate/EnterpriseDepartmentCreate';
import EnterpriseDepartmentDetails from 'src/components/EnterpriseDepartmentDetails/EnterpriseDepartmentDetails';
import EnterpriseDepartments from 'src/components/EnterpriseDepartments/EnterpriseDepartments';

import EnterpriseLocations from 'src/components/enterprise/features/Locations/Locations';
import { EnterpriseManageLocation } from 'src/components/enterprise/features/Locations/ManageLocation/ManageLocation';
import EnterpriseLocationEmployees from 'src/components/enterprise/features/Locations/LocationEmployees/LocationEmployees';

import EnterpriseGroups from 'src/components/enterprise/EnterpriseGroups/EnterpriseGroups';
import EnterpriseManageGroup from 'src/components/enterprise/EnterpriseGroups/EnterpriseManageGroup';
import EnterpriseGroupDetails from 'src/components/enterprise/EnterpriseGroups/EnterpriseGroupDetails';

import EnterpriseInvoiceRecipients from 'src/components/enterprise/EnterpriseInvoiceRecipients/EnterpriseInvoiceRecipients';
import EnterpriseInvoices from 'src/components/EnterpriseInvoices/EnterpriseInvoices';

import OrderHistory from 'src/components/enterprise/features/OrderHistory/OrderHistory';

import OrderReceiptContainer from 'src/components/enterprise/features/OrderHistory/OrderReceipt/OrderReceiptContainer';

import EnterprisePayments from 'src/components/enterprise/EnterprisePayments/EnterprisePayments';

import { EnterpriseSignupStatusProvider } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { EnterpriseSignupWizard } from 'src/components/EnterpriseSignupWizard/EnterpriseSignupWizard';

import TaxExemptions from 'src/components/enterprise/features/TaxExemptions/TaxExemptions';

import { ParentAccounts } from 'src/components/enterprise/Parents/Accounts/ParentAccounts';
import { ParentAdmins } from 'src/components/enterprise/Parents/Admin/ParentAdmins';
import { AddParentAdmin } from 'src/components/enterprise/Parents/Admin/AddParentAdmin';
import { ParentAdminOverview } from 'src/components/enterprise/Parents/Admin/ParentAdminOverview';
import { EditParentAdmin } from 'src/components/enterprise/Parents/Admin/EditParentAdmin';

import ChildAccountCreationController from 'src/components/enterprise/Parents/Children/ParentChildAccountController/ChildAccountCreationController';
import { ParentChildAccounts } from 'src/components/enterprise/Parents/Admin/ParentChildAccounts';

import { CorporateAccounts } from 'src/components/CorporateAccounts/CorporateAccounts';
import { VariantSelection } from 'src/components/Variants/VariantSelection';
import { EmployeeRoutes } from 'src/components/AppRoutes/EmployeeRoutes';
import { EmployeePaths } from 'src/components/AppRoutes/Enterprise/EmployeePaths';
import { ReportingPaths } from 'src/components/AppRoutes/Enterprise/ReportingPaths';
import { EnterpriseHomePage } from 'src/components/enterprise/features/HomePage/EnterpriseHomePage';
import { ReportingAccessGate } from 'src/components/enterprise/features/Reporting/Routes/ReportingAccessGate/ReportingAccessGate';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { ParentRoutes } from './ParentRoutes';
import { ParentPaths } from './Parent/ParentPaths';

export const EnterpriseRoutes = (): JSX.Element => {
  const { value: enableReportingFeature } = useFeatureFlag(
    FeatureFlag.ENABLE_REPORTING_FEATURE
  );

  const EnterpriseHomePageLayout = withEnterpriseLayout(EnterpriseHomePage);
  const DepartmentsCreatePage = withEnterpriseLayout(
    EnterpriseDepartmentCreate
  );
  const DepartmentsDetailsPage = withEnterpriseLayout(
    EnterpriseDepartmentDetails
  );
  const DepartmentsLandingPage = withEnterpriseLayout(EnterpriseDepartments);

  const EnterpriseLocationsPage = withEnterpriseLayout(EnterpriseLocations);
  const EnterpriseManageLocationCreatePage = withEnterpriseLayout(
    EnterpriseManageLocation
  );

  const EnterpriseManageLocationEditPage = withEnterpriseLayout(
    EnterpriseManageLocation
  );
  const EnterpriseLocationEmployeesPage = withEnterpriseLayout(
    EnterpriseLocationEmployees
  );

  const EnterpriseGroupsPage = withEnterpriseLayout(EnterpriseGroups);
  const EnterpriseManageGroupCreatePage = withEnterpriseLayout(
    EnterpriseManageGroup
  );

  const EnterpriseManageGroupEditPage = withEnterpriseLayout(
    EnterpriseManageGroup
  );
  const EnterpriseGroupDetailsPage = withEnterpriseLayout(
    EnterpriseGroupDetails
  );

  const EnterpriseInvoiceRecipientsPage = withEnterpriseLayout(
    EnterpriseInvoiceRecipients
  );

  const EnterpriseInvoicesPage = withEnterpriseLayout(EnterpriseInvoices);

  const OrderHistoryPage = withEnterpriseLayout(OrderHistory);

  const OrderReceiptContainerPage = withEnterpriseLayout(OrderReceiptContainer);

  const EnterprisePaymentsPage = withEnterpriseLayout(EnterprisePayments);

  const TaxExemptionsPage = withEnterpriseLayout(TaxExemptions);

  const EmptyPage = withEnterpriseLayout(() => <></>);

  // Parent Admin pages

  const ParentAdminsPage = withEnterpriseLayout(ParentAdmins);
  const AddParentAdminPage = withEnterpriseLayout(AddParentAdmin);
  const ParentAdminOverviewPage = withEnterpriseLayout(ParentAdminOverview);
  const EditParentAdminPage = withEnterpriseLayout(EditParentAdmin);

  const ParentAccountsPage = withEnterpriseLayout(ParentAccounts);

  const ChildAccountCreationControllerPage = withEnterpriseLayout(
    ChildAccountCreationController
  );
  const ParentChildAccountsPage = withEnterpriseLayout(ParentChildAccounts);

  const CorporateAccountsPage = withEnterpriseLayout(CorporateAccounts);
  const VariantSelectionPage = withEnterpriseLayout(VariantSelection);

  return (
    <Switch>
      <WrappedRoute
        path={EnterprisePaths.home}
        component={() => <EnterpriseHomePageLayout pageTitle="Home" />}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/departments/create"
        component={() => (
          <DepartmentsCreatePage pageTitle="Departments | Create" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/departments/:departmentId"
        component={() => (
          <DepartmentsDetailsPage pageTitle="Departments Details" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/departments"
        component={() => <DepartmentsLandingPage pageTitle="Departments" />}
      />

      {/* locations */}
      <WrappedRoute
        path="/corporate/enterprise/:corpid/locations/:locationId/employees"
        component={() => (
          <EnterpriseLocationEmployeesPage pageTitle="Location | Employees" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/locations/:locationId/edit"
        component={() => (
          <EnterpriseManageLocationEditPage
            pageTitle="Location | Edit"
            isEditing
          />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/locations/create"
        component={() => (
          <EnterpriseManageLocationCreatePage pageTitle="Location | Create" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/locations"
        component={() => <EnterpriseLocationsPage pageTitle="Locations" />}
      />

      {/* groups */}
      <WrappedRoute
        path="/corporate/enterprise/:corpid/groups/:groupId/details"
        component={() => (
          <EnterpriseGroupDetailsPage pageTitle="Groups | Details" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/groups/:groupId/edit"
        component={() => (
          <EnterpriseManageGroupEditPage pageTitle="Groups | Edit" isEditing />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/groups/create"
        component={() => (
          <EnterpriseManageGroupCreatePage pageTitle="Groups | Create" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/groups"
        component={() => <EnterpriseGroupsPage pageTitle="Groups" />}
      />

      <WrappedRoute
        path="/corporate/enterprise/:corpid/invoice-notifications"
        component={() => (
          <EnterpriseInvoiceRecipientsPage pageTitle="Invoice Recipients" />
        )}
      />

      <WrappedRoute
        path="/corporate/enterprise/:corpid/invoices"
        component={() => <EnterpriseInvoicesPage pageTitle="Invoices" />}
      />

      <WrappedRoute
        path={EnterprisePaths.mealCredits}
        component={MealCreditRoutes}
      />

      <WrappedRoute path={CorporatePaths.base} component={CorporateRoutes} />

      <WrappedRoute
        path="/corporate/enterprise/:corpid/order-history"
        component={() => (
          <OrderHistoryPage pageTitle="Order history" useFluidContainer />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/order-receipt/:orderId"
        component={() => (
          <OrderReceiptContainerPage
            pageTitle="Order Receipt"
            useFluidContainer
          />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/payments"
        component={() => <EnterprisePaymentsPage pageTitle="Payments" />}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/set-up/step/:stepNumber"
        component={() => (
          <EnterpriseSignupStatusProvider>
            <EnterpriseSignupWizard />
          </EnterpriseSignupStatusProvider>
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/:corpid/tax-exemptions"
        component={() => <TaxExemptionsPage pageTitle="Tax Exemptions" />}
      />

      {enableReportingFeature && (
        <WrappedRoute
          path={ReportingPaths.base}
          component={ReportingAccessGate}
        />
      )}

      <WrappedRoute path={EmployeePaths.base} component={EmployeeRoutes} />

      {/* Parent admins */}
      <WrappedRoute
        exact
        path="/corporate/enterprise/parents/accounts"
        component={() => <ParentAccountsPage pageTitle="Parent admins" />}
      />
      <WrappedRoute
        path="/corporate/enterprise/parents/:corpid/admin"
        exact
        component={() => <ParentAdminsPage pageTitle="Parent admins" />}
      />

      <WrappedRoute
        path="/corporate/enterprise/parents/:corpid/admin/create"
        component={() => (
          <AddParentAdminPage pageTitle="Parent Child Accounts" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/parents/:corpid/admin/:dinerId/edit"
        component={() => (
          <EditParentAdminPage pageTitle="Parent Child Accounts" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/parents/:corpid/admin/:dinerId"
        component={() => (
          <ParentAdminOverviewPage pageTitle="Parent admin overview" />
        )}
      />

      {/* Children */}
      <WrappedRoute
        exact
        path="/corporate/enterprise/parents/:corpid/children/create"
        component={() => (
          <ChildAccountCreationControllerPage pageTitle="Parent Child Account Creation" />
        )}
      />
      <WrappedRoute
        exact
        path="/corporate/enterprise/parents/:corpid/children"
        component={() => (
          <ParentChildAccountsPage pageTitle="Parent Child Accounts" />
        )}
      />

      <WrappedRoute path={ParentPaths.base} component={ParentRoutes} />

      <WrappedRoute
        path="/corporate/enterprise/corporations"
        component={() => (
          <CorporateAccountsPage pageTitle="Corporate Accounts" />
        )}
      />
      <WrappedRoute
        path="/corporate/enterprise/toggles"
        component={() => <VariantSelectionPage pageTitle="Variant toggles" />}
      />
      {/* empty page */}
      <WrappedRoute
        path="/corporate/enterprise/:corpid"
        component={() => <EmptyPage pageTitle="" />}
      />
    </Switch>
  );
};
